import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  addToCart as addToCartAction,
  clearCart as clearCartAction,
  decreaseQuantity as decreaseQuantityAction,
  increaseQuantity as increaseQuantityAction,
  removeFromCart as removeFromCartAction,
  setMenuId as setMenuIdAction,
  setCartId as setCartIdAction,
  setUusid as setUusidAction,
  setCartStoreId as setCartStoreIdAction,
  setCartItems as setCartItemsAction,
  setIsLoading as setIsLoadingAction,
  updateCartItem as updateCartItemAction,
  validateCart as validateCartThunk,
  selectCartTotals,
} from "../store/cartSlice";
import { resetOrderTypeToDefault as resetOrderTypeToDefaultAction } from "../store/orderOptionSlice";
import {
  CartItemModel,
  CartItemModifierGroupModel,
  StoreProductModel,
} from "../models";
import { getStoreIdFromUrl } from "../helpers";

export function useCart() {
  const dispatch = useAppDispatch();

  const { cartId, isLoading, items, cartStoreId, uusid, menuId: cartMenuId } = useAppSelector(
    (state) => state.cart,
  );

  const totals = useAppSelector(selectCartTotals);

  const cartQuantity = useMemo(
    () => items.reduce((quantity, item) => item.product_qty + quantity, 0),
    [items],
  );

  const isProductExists = useCallback(
    (id: string): boolean => items.some((item) => item.product_id === id),
    [items],
  );

  const setUusid = useCallback(
    (value: number) => {
      dispatch(setUusidAction(value));
    },
    [dispatch],
  );

  const setCartId = useCallback(
    (id: string) => {
      dispatch(setCartIdAction(id));
    },
    [dispatch],
  );

  const setMenuId = useCallback(
    (id: string) => {
      dispatch(setMenuIdAction(id));
    },
    [dispatch],
  );

  const setIsLoading = useCallback(
    (value: boolean) => {
      dispatch(setIsLoadingAction(value));
    },
    [dispatch],
  );

  const setCartItems = useCallback(
    (cartItems: CartItemModel[]) => {
      dispatch(setCartItemsAction(cartItems));
    },
    [dispatch],
  );

  const addToCart = useCallback(
    (
      product: StoreProductModel,
      quantity: number = 1,
      modifiers: CartItemModifierGroupModel[] = [],
      instructions: string = "",
      menuId: string,
    ) => {
      if (cartMenuId && cartMenuId !== menuId) {
        dispatch(resetOrderTypeToDefaultAction());
      }
      dispatch(
        addToCartAction({ product, quantity, modifiers, instructions, menuId }),
      );
    },
    [dispatch, cartMenuId],
  );

  const increaseQuantity = useCallback(
    (value: number | string) => {
      dispatch(increaseQuantityAction(value));
      dispatch(validateCartThunk({}));
    },
    [dispatch],
  );

  const decreaseQuantity = useCallback(
    (value: number | string) => {
      dispatch(decreaseQuantityAction(value));
      dispatch(validateCartThunk({}));
    },
    [dispatch],
  );

  const removeFromCart = useCallback(
    (index: number) => {
      dispatch(removeFromCartAction(index));
      dispatch(validateCartThunk({}));
    },
    [dispatch],
  );

  const clearCart = useCallback(() => {
    dispatch(clearCartAction());
  }, [dispatch]);

  const getCartItem = useCallback(
    (id: string): CartItemModel | undefined => {
      return items.find((i) => i.product_id === id);
    },
    [items],
  );

  const updateCartItem = useCallback(
    (index: number, cartItem: CartItemModel) => {
      dispatch(updateCartItemAction({ index, cartItem }));
      dispatch(validateCartThunk({}));
    },
    [dispatch],
  );

  // manual trigger if you want to validate cart before checkout
  const validateCart = useCallback(() => {
    dispatch(validateCartThunk({}));
  }, [dispatch]);

  // 🔁 mimic your old useEffect: reset cart when storeId changes
  const storeId = getStoreIdFromUrl();

  useEffect(() => {
    if (!cartStoreId) {
      dispatch(setCartStoreIdAction(storeId));
    } else if (cartStoreId !== storeId) {
      dispatch(clearCartAction());
      dispatch(setCartStoreIdAction(storeId));
    }
  }, [storeId, cartStoreId, dispatch]);

  return {
    cartId,
    setCartId,
    setMenuId,
    isLoading,
    setIsLoading,
    isProductExists,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    cartQuantity,
    cartItems: items,
    setCartItems,
    getCartItem,
    updateCartItem,
    clearCart,
    validateCart,
    uusid,
    setUusid,

    // calculated totals based on validateCart response fields
    cartPrice: totals.price,
    cartDiscount: totals.discount,
    cartInflation: totals.inflation,
    cartTax: totals.tax,
    cartSalePrice: totals.salePrice,
  };
}
